import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { TaskDto } from "../features/tasks/types";
import { partition } from "../helpers/helpers";

// A due date is a commitment: tasks with one always stay visible, even if
// they were stored away (before the rule, or before they got a due date)
export const isTaskHidden = (task: Pick<TaskDto, "storeStatus" | "dueDate">) =>
  task.storeStatus === "STOREAWAY" && !task.dueDate;

export const getTaskPriorityValue = (taskData: TaskDto) => {
  return (
    Math.sqrt(
      taskData.urgency * taskData.urgency +
        taskData.importance * taskData.importance,
    ) / Math.sqrt(2)
  );
};

export const useFilteredTasks = (specificTasks?: TaskDto[]) => {
  // Selectors
  const {
    tasks: tasksFromSelector,
    completedTasks,
    meta: tasksMeta,
  } = useSelector((state: RootState) => state.tasks);

  // Standarized - if it ever changes or is set in settings you don't have to tweak it everywhere

  const activeAccessRoles = useSelector(
    (state: RootState) => state.organizations.activeAccessRoles,
  );
  const userId = useSelector((state: RootState) => state.user.userData.id);

  const isSpecific = !!specificTasks;
  const baseTasks = isSpecific ? specificTasks! : tasksFromSelector;

  // Memoized calculations
  const {
    accessibleTasks,
    visibleTasks,
    hiddenTasks,
    sortedVisibleTasks,
    tasksWithoutScheduling,
    scheduleableTasks,
    nonScheduleTasks,
    planningTasks,
    sortedAccessibleTasks,
    q2tasks,
    q3tasks,
    q4tasks,
  } = useMemo(() => {
    const visible: TaskDto[] = [];
    const hidden: TaskDto[] = [];
    const unscheduled: TaskDto[] = [];
    const planning: TaskDto[] = [];

    const accessibleTasks = isSpecific
      ? baseTasks // ← don’t apply access filtering when specific tasks are passed
      : baseTasks
          .filter((task) => {
            const noOrganizationAccess =
              activeAccessRoles.includes("0") &&
              (!task.NoteAccess || task.NoteAccess.length === 0);

            const hasSharedAccess = task.NoteAccess?.some(
              (access) =>
                access?.userId === userId ||
                activeAccessRoles.includes(access.groupId as string) ||
                activeAccessRoles.includes(access.organizationId as string),
            );
            return hasSharedAccess || noOrganizationAccess;
          })
          .sort(
            (a, b) =>
              new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
          );

    for (const task of accessibleTasks) {
      if (isTaskHidden(task)) {
        hidden.push(task);
      } else {
        visible.push(task);

        if (task.scheduledStatus === "OPEN") {
          unscheduled.push(task);
        }

        if (task.scheduledStatus === "PLANNING") {
          planning.push(task);
        }
      }
    }

    const sortedAccessibleTasks = [...accessibleTasks].sort(
      (a, b) => getTaskPriorityValue(b) - getTaskPriorityValue(a),
    );

    const sortedVisible = [...visible].sort(
      (a, b) => getTaskPriorityValue(b) - getTaskPriorityValue(a),
    );

    const [schedulable, nonSchedulable] = partition(
      unscheduled,
      (task) => task.urgency <= 50 && task.importance >= 50,
    );

    const q2tasks = sortedVisible.filter(
      (task) => task.urgency <= 50 && task.importance >= 50,
    );

    const q3tasks = sortedVisible.filter(
      (task) => task.importance < 50 && task.urgency > 50,
    );

    const q4tasks = sortedVisible.filter(
      (task) => task.importance < 50 && task.urgency < 50,
    );

    return {
      accessibleTasks,
      sortedAccessibleTasks,
      visibleTasks: visible,
      q2tasks: q2tasks,
      q3tasks: q3tasks,
      q4tasks: q4tasks,
      hiddenTasks: hidden,
      sortedVisibleTasks: sortedVisible,
      tasksWithoutScheduling: unscheduled,
      scheduleableTasks: schedulable,
      nonScheduleTasks: nonSchedulable,
      planningTasks: planning,
    };
  }, [baseTasks, isSpecific, activeAccessRoles, userId, tasksFromSelector]);

  const taskAmount = visibleTasks.length;

  const selectedRolesCount = activeAccessRoles.length;

  const priorityFilters = useMemo(() => {
    const must: TaskDto[] = [];
    const should: TaskDto[] = [];
    const could: TaskDto[] = [];
    const want: TaskDto[] = [];

    for (const task of sortedVisibleTasks) {
      const { importance, urgency } = task;

      if (importance > 50 && urgency > 50) {
        must.push(task);
      } else if (
        importance > 101 - urgency &&
        ((urgency <= 50 && importance >= 50) ||
          (urgency >= 50 && importance <= 50))
      ) {
        should.push(task);
      } else if (
        importance < 101 - urgency &&
        ((urgency <= 50 && importance >= 50) ||
          (urgency >= 50 && importance <= 50))
      ) {
        could.push(task);
      } else if (urgency < 55 && importance < 50) {
        want.push(task);
      }
    }

    return {
      mustTasks: must,
      shouldTasks: should,
      couldTasks: could,
      wantToDoTasks: want,
    };
  }, [sortedVisibleTasks, activeAccessRoles]);

  const getTasksForRole = useCallback(
    (roleIds: string[]) => {
      return baseTasks.filter((task) => {
        const hasAccess = task.NoteAccess?.some(
          (access) =>
            roleIds.includes(access.groupId as string) ||
            roleIds.includes(access.organizationId as string),
        );
        return hasAccess && !isTaskHidden(task);
      });
    },
    [baseTasks],
  );

  const getHandlingRecommendation = useCallback((task: TaskDto) => {
    const { importance, urgency } = task;
    if (importance > 50 && urgency > 50) return "Do";
    if (importance >= 50 && urgency <= 50) return "Schedule";
    if (importance < 50 && urgency >= 50) return "Delegate";
    return "Archive";
  }, []);

  const getTaskPriority = useCallback((task: TaskDto) => {
    const { importance, urgency } = task;

    if (urgency > 50 && importance > 50) return 0;

    const balancedCondition =
      (urgency <= 50 && importance >= 50) ||
      (urgency >= 50 && importance <= 50);

    if (importance > 101 - urgency && balancedCondition) return 1;
    if (importance < 101 - urgency && balancedCondition) return 2;

    return 3;
  }, []);

  const getTaskColor = useCallback((taskData: TaskDto) => {
    const q = getHandlingRecommendation(taskData);
    if (q === "Archive") return "var(--color__green500)";
    else if (q === "Do") return "var(--color__red500)";
    else if (q === "Schedule") return "var(--color__blue500)";
    else return "var(--color__orange500)";
  }, []);

  const getTaskBackgroundColor = useCallback((taskData: TaskDto) => {
    const q = getHandlingRecommendation(taskData);
    if (q === "Archive") return "bg_green300";
    else if (q === "Do") return "bg_red300";
    else if (q === "Schedule") return "bg_blue300";
    else return "bg_orange300";
  }, []);

  return {
    tasksFromSelector,
    visibleTasks,
    hiddenTasks,
    accessibleTasks,
    sortedVisibleTasks,
    taskAmount,
    selectedRolesCount,
    completedTasks,
    tasksMeta,
    getTasksForRole,
    getHandlingRecommendation,
    getTaskPriority,
    getTaskColor,
    getTaskPriorityValue,
    getTaskBackgroundColor,
    sortedAccessibleTasks,
    tasksWithoutScheduling,
    scheduleableTasks,
    nonScheduleTasks,
    q2tasks,
    q3tasks,
    q4tasks,
    planningTasks,
    ...priorityFilters,
  };
};
